<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import { UserRound } from '@lucide/vue';
import { Badge } from '@/components/ui/badge';
import RegistrationInfo from '@/components/registration/RegistrationInfo.vue';
import { formatMoney } from '@/utils/money';

const props = withDefaults(defineProps<{
  order: Record<string, any>,
  /** The current search term, so the matched names can be highlighted. */
  search?: string,
  /**
   * Where the order opens. A listing embedded in another page sends it to a new tab, so
   * the reader keeps the page they were working on.
   */
  target?: '_self' | '_blank',
}>(), {
  search: '',
  target: '_self',
});

const statusVariant = computed(() => {
  switch (props.order.status.value) {
    case 'new':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'zinc-light';
  }
});

/**
 * The event's own clock, as every other listing renders its local_* dates, but kept in
 * two parts: together they are 'lll', and stacked below xl they save the column the
 * width of a whole date and time. The table scrolls rather than compressing, so the
 * break has to be asked for; it will not happen on its own.
 */
const createdOn = computed(() => dayjs(props.order.local_created_at));

const createdDate = computed(() => createdOn.value.format('ll'));

const createdTime = computed(() => createdOn.value.format('LT'));

/**
 * The listing is worked from an iPad, where a small link is a poor target, so the whole
 * row opens the order. The order number stays a real link, so it can still be opened in
 * a new tab or reached with a keyboard.
 */
function openOrder(event: MouseEvent) {
  // Selecting text inside a cell should not navigate away from it.
  if (window.getSelection()?.toString()) {
    return;
  }

  if (props.target === '_blank' || event.metaKey || event.ctrlKey) {
    window.open(props.order.links.edit, '_blank');

    return;
  }

  window.location.assign(props.order.links.edit);
}

/**
 * The cell stands three lines tall at most, the count of those left out included: three
 * participants are all named, a fourth costs the third its line. The full list stays
 * reachable as the title of that count.
 */
const PARTICIPANT_LINES = 3;

const participants = computed(() => props.order.participants ?? []);

const shownParticipants = computed(() => participants.value.length > PARTICIPANT_LINES
  ? participants.value.slice(0, PARTICIPANT_LINES - 1)
  : participants.value);

const hiddenParticipantCount = computed(() => participants.value.length - shownParticipants.value.length);

const allParticipantNames = computed(
  () => participants.value.map((participant) => `${participant.firstname} ${participant.lastname}`).join(', ')
);


/** Highlighted in red when the order still owes something. */
const isUnderpaid = computed(() => {
  if (!props.order.total_with_tax) {
    return false;
  }

  return parseFloat(props.order.total_with_tax.amount) > parseFloat(props.order.paid_amount.amount);
});
</script>

<template>
  <tr class="tw:cursor-pointer" @click="openOrder">
    <td class="tw:align-top">
      <a :href="order.links.edit" :target="props.target" class="tw:font-bold" @click.stop>#{{ order.id }}</a>
    </td>

    <td class="tw:align-top tw:text-zinc-500">
      <span class="tw:block tw:whitespace-nowrap tw:xl:inline tw:xl:mr-1">{{ createdDate }}</span>
      <span class="tw:block tw:whitespace-nowrap tw:xl:inline">{{ createdTime }}</span>
    </td>

    <td class="tw:align-top">
      <RegistrationInfo
        v-if="order.registration"
        compact
        :registration="order.registration"
        :highlight="search"
      />
      <span v-else class="tw:text-zinc-400">{{ $t('No customer') }}</span>
    </td>

    <td class="tw:align-middle tw:text-center">
      <Badge variant="zinc-light" class="tw:text-base">{{ order.order_items_count }}</Badge>
    </td>

    <td class="tw:align-top">
      <template v-if="shownParticipants.length">
        <div
          v-for="participant in shownParticipants"
          :key="participant.id"
          class="tw:flex tw:items-center tw:gap-1.5"
        >
          <UserRound class="tw:size-3.5 tw:shrink-0 tw:text-zinc-400" />
          <text-highlight :query="search" :splitBySpace="true">
            {{ participant.firstname }} {{ participant.lastname }}
          </text-highlight>
        </div>
        <div
          v-if="hiddenParticipantCount > 0"
          class="tw:pl-5 tw:text-zinc-500"
          :title="allParticipantNames"
        >
          {{ $t('+ {count} more', { count: hiddenParticipantCount }) }}
        </div>
      </template>
    </td>

    <td class="tw:align-top">
      <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
        <Badge :variant="statusVariant">{{ order.status.label }}</Badge>
        <Badge v-if="order.deleted_at" variant="red">{{ $t('Deleted') }}</Badge>
      </div>
    </td>

    <td class="tw:align-top tw:text-right tw:tabular-nums">
      <span class="tw:font-semibold tw:text-zinc-900">{{ formatMoney(order.total_with_tax) }}</span>
      <small
        v-if="order.total_with_tax"
        class="tw:block"
        :class="isUnderpaid ? 'tw:text-red-600' : 'tw:text-zinc-500'"
      >
        {{ formatMoney(order.paid_amount) }}
      </small>
    </td>

  </tr>
</template>
